import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ClipboardList, RefreshCw, ToggleLeft, ToggleRight, CheckSquare, X } from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { countingListCreationService } from '../../../../services/countingListCreationService';
import {
    ItemBinCountingRow,
    CountingListPagedParams,
    CountingListFilterSnapshot,
    CountingListSelection
} from '../../../../types/countingListCreation';
import { CreateCountingListModal } from './CreateCountingListModal';
import { WarehouseTable } from '../../../shared/table';
import { createColumnFactory } from '../../../shared/table/utils/tableUtils';
import type { ColumnDefinition, TableAction, ServerFetchParams } from '../../../shared/table/types/table';
import { BackButton } from '../../../common/BackButton';

interface CountingListCreationProps {
    onBack: () => void;
}

/** Filterfeltene som sendes til server — samme liste som handleServerFetch mapper. */
const FILTER_KEYS: (keyof CountingListFilterSnapshot)[] = [
    'itemNo', 'description1', 'itemBarCode', 'supplierItemNo',
    'itemGrp1', 'itemGrp2', 'itemGrp3',
    'warehouseID', 'binCode', 'itemUnitCode', 'stdLocationName',
    'binQuantity', 'lastCountedDate', 'lastMovementDate'
];

/**
 * «Velg alle rader som matcher filteret» — utvalget beskrives av filteret, ikke av radnøkler.
 * Radene hentes på server ved opprettelse, så antall sider spiller ingen rolle.
 */
interface FilterSelectionState {
    filters: CountingListFilterSnapshot;
    /** Antall rader tabellen rapporterte da utvalget ble gjort (til visning i banner/knapp). */
    totalRows: number;
}

export const CountingListCreation: React.FC<CountingListCreationProps> = ({ onBack }) => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const companyNo = user?.companyNo || '';

    // Server-side pagination state
    const [serverData, setServerData] = useState<ItemBinCountingRow[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Utvalg — to moduser:
    //  - rader: eksplisitt valgte rader, husket som hele rader (ikke bare nøkler) slik at valg på
    //    tvers av sider overlever sidebytte. Tidligere ble nøklene slått opp mot gjeldende side ved
    //    opprettelse, og rader fra andre sider falt stille bort.
    //  - filter: «alle rader som matcher filteret», med valgfrie manuelle fravalg (excludedRowKeys).
    const [selectedRowsById, setSelectedRowsById] = useState<Map<string, ItemBinCountingRow>>(() => new Map());
    const [filterSelection, setFilterSelection] = useState<FilterSelectionState | null>(null);
    const [excludedRowKeys, setExcludedRowKeys] = useState<Set<string>>(() => new Set());
    const [showCreateModal, setShowCreateModal] = useState(false);

    // Include zero stock toggle
    const [includeZeroStock, setIncludeZeroStock] = useState(false);

    // Include rows already on an active counting list (shown dimmed, not selectable)
    const [includeOnActiveList, setIncludeOnActiveList] = useState(false);

    // Fetch refs
    const isLoadingRef = useRef(false);
    const initialLoadDoneRef = useRef(false);
    const latestFetchParamsRef = useRef<CountingListPagedParams>({ pageNumber: 1, pageSize: 50 });
    const filterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Column factory
    const columnFactory = useMemo(() => createColumnFactory<ItemBinCountingRow>(), []);

    const clearFilterSelection = useCallback(() => {
        setFilterSelection(null);
        setExcludedRowKeys(new Set());
    }, []);

    // Fetch paged data from server
    const fetchPagedData = useCallback(async (params: CountingListPagedParams) => {
        if (!companyNo) return;
        if (isLoadingRef.current) return;
        isLoadingRef.current = true;

        if (!initialLoadDoneRef.current) {
            setIsLoading(true);
        }
        setError(null);

        try {
            const result = await countingListCreationService.getItemBinsPaged({
                ...params,
                includeZeroStock,
                includeOnActiveCountList: includeOnActiveList
            });

            setServerData(result.data);
            setTotalRecords(result.totalRecords);
            latestFetchParamsRef.current = params;
            initialLoadDoneRef.current = true;
        } catch (err) {
            console.error('Error loading item bins:', err);
            setError(t('manager.modules.countingListCreation.messages.loadError', {}, 'Kunne ikke laste vare-/lokasjonsdata. Prøv igjen.'));
        } finally {
            setIsLoading(false);
            isLoadingRef.current = false;
        }
    }, [companyNo, includeZeroStock, includeOnActiveList, t]);

    // Server-side pagination callback
    const handleServerFetch = useCallback((params: ServerFetchParams) => {
        const f = params.filters;
        const queryParams: CountingListPagedParams = {
            pageNumber: params.pageNumber,
            pageSize: params.pageSize,
            sortColumn: params.sortColumn,
            sortDirection: params.sortDirection,
            itemNo: f?.itemNo || undefined,
            description1: f?.description1 || undefined,
            itemBarCode: f?.itemBarCode || undefined,
            supplierItemNo: f?.supplierItemNo || undefined,
            itemGrp1: f?.itemGrp1 || undefined,
            itemGrp2: f?.itemGrp2 || undefined,
            itemGrp3: f?.itemGrp3 || undefined,
            warehouseID: f?.warehouseID || undefined,
            binCode: f?.binCode || undefined,
            itemUnitCode: f?.itemUnitCode || undefined,
            stdLocationName: f?.stdLocationName || undefined,
            binQuantity: f?.binQuantity || undefined,
            lastCountedDate: f?.lastCountedDate || undefined,
            lastMovementDate: f?.lastMovementDate || undefined,
        };

        // Debounce filter changes
        if (filterDebounceRef.current) {
            clearTimeout(filterDebounceRef.current);
        }

        const prevFilters = latestFetchParamsRef.current;
        const isFilterChange = params.pageNumber === 1 &&
            FILTER_KEYS.some(key => queryParams[key] !== prevFilters[key]);

        if (isFilterChange) {
            // Et filterbasert utvalg gjelder filteret det ble gjort med — nytt filter, nytt utvalg
            clearFilterSelection();
            filterDebounceRef.current = setTimeout(() => {
                fetchPagedData(queryParams);
            }, 300);
        } else {
            fetchPagedData(queryParams);
        }
    }, [fetchPagedData, clearFilterSelection]);

    // Refresh helper
    const refreshData = useCallback(() => {
        fetchPagedData(latestFetchParamsRef.current);
    }, [fetchPagedData]);

    // Initial load — kun første gang. fetchPagedData får ny identitet når en toggle
    // endres; uten guarden ville denne effekten da re-kjørt med nakne parametre og
    // mistet aktive kolonnefiltre/sortering (toggle-effekten under håndterer re-fetch).
    useEffect(() => {
        if (!initialLoadDoneRef.current) {
            fetchPagedData({ pageNumber: 1, pageSize: 50 });
        }
    }, [fetchPagedData]);

    // Cleanup debounce timer
    useEffect(() => {
        return () => {
            if (filterDebounceRef.current) {
                clearTimeout(filterDebounceRef.current);
            }
        };
    }, []);

    // Re-fetch when a toggle changes — bryterne er del av filteret, så filterutvalget nullstilles
    useEffect(() => {
        if (initialLoadDoneRef.current) {
            clearFilterSelection();
            fetchPagedData({ ...latestFetchParamsRef.current, pageNumber: 1 });
        }
    }, [includeZeroStock, includeOnActiveList, fetchPagedData, clearFilterSelection]);

    // Row ID
    const getRowId = useCallback((row: ItemBinCountingRow) => row.rowKey, []);

    // Rader på gjeldende side som kan velges (ikke på aktiv telleliste)
    const selectablePageKeys = useMemo(
        () => serverData.filter(row => !row.activeCountListBatchID).map(row => row.rowKey),
        [serverData]
    );

    // Nøklene tabellen skal vise som valgt
    const selectedRowKeys = useMemo<string[]>(() => {
        if (filterSelection) {
            return selectablePageKeys.filter(key => !excludedRowKeys.has(key));
        }
        return Array.from(selectedRowsById.keys());
    }, [filterSelection, selectablePageKeys, excludedRowKeys, selectedRowsById]);

    // Selection change fra tabellen. selectedIds er unionen over alle sider (kontrollert utvalg).
    const handleSelectionChange = useCallback((selectedIds: (string | number)[], _selectedData: ItemBinCountingRow[]) => {
        const selectedSet = new Set(selectedIds.map(id => String(id)));
        const rowsOnPage = new Map(serverData.map(row => [row.rowKey, row]));

        if (filterSelection) {
            // Filtermodus: avkrysning på denne siden styrer kun manuelle fravalg
            setExcludedRowKeys(prev => {
                const next = new Set(prev);
                for (const key of selectablePageKeys) {
                    if (selectedSet.has(key)) next.delete(key);
                    else next.add(key);
                }
                return next;
            });
            return;
        }

        // Radmodus: husk hele raden, aldri rader som ligger på aktiv telleliste (dekker også «velg alle»)
        setSelectedRowsById(prev => {
            const next = new Map<string, ItemBinCountingRow>();
            selectedSet.forEach(key => {
                const row = rowsOnPage.get(key) ?? prev.get(key);
                if (row && !row.activeCountListBatchID) {
                    next.set(key, row);
                }
            });
            return next;
        });
    }, [serverData, filterSelection, selectablePageKeys]);

    // Banner: alle valgbare rader på siden er valgt, og det finnes flere sider
    const allOnPageSelected = selectablePageKeys.length > 0
        && selectablePageKeys.every(key => selectedRowsById.has(key));
    const hasMorePages = totalRecords > serverData.length;
    const showSelectAllBanner = !filterSelection && allOnPageSelected && hasMorePages;

    const handleSelectAllMatching = useCallback(() => {
        const p = latestFetchParamsRef.current;
        const filters: CountingListFilterSnapshot = { includeZeroStock, includeOnActiveCountList: false };
        for (const key of FILTER_KEYS) {
            const value = p[key];
            if (value !== undefined && value !== null && value !== '') {
                (filters as Record<string, unknown>)[key] = value;
            }
        }
        setSelectedRowsById(new Map());
        setExcludedRowKeys(new Set());
        setFilterSelection({ filters, totalRows: totalRecords });
    }, [includeZeroStock, totalRecords]);

    const handleClearSelection = useCallback(() => {
        clearFilterSelection();
        setSelectedRowsById(new Map());
    }, [clearFilterSelection]);

    // Antall til knapp/banner (omtrentlig i filtermodus — modalen viser eksakt tall fra server)
    const selectedCount = filterSelection
        ? Math.max(0, filterSelection.totalRows - excludedRowKeys.size)
        : selectedRowsById.size;

    // Handle create button
    const handleCreateCountingList = useCallback(() => {
        if (selectedCount === 0) {
            alert(t('manager.modules.countingListCreation.messages.selectAtLeastOne', {}, 'Velg minst én vare/lokasjon for å opprette telleliste'));
            return;
        }
        setShowCreateModal(true);
    }, [selectedCount, t]);

    // Handle creation success
    const handleCreateSuccess = useCallback(() => {
        refreshData();
        handleClearSelection();
        setShowCreateModal(false);
    }, [refreshData, handleClearSelection]);

    // Utvalget slik modalen skal ha det
    const selection = useMemo<CountingListSelection>(() => {
        if (filterSelection) {
            return {
                mode: 'filter',
                filters: filterSelection.filters,
                excludeRowKeys: Array.from(excludedRowKeys),
                totalRows: filterSelection.totalRows
            };
        }
        return { mode: 'rows', rows: Array.from(selectedRowsById.values()) };
    }, [filterSelection, excludedRowKeys, selectedRowsById]);

    // Column definitions
    const columns: ColumnDefinition<ItemBinCountingRow>[] = useMemo(() => [
        columnFactory.createTextColumn('itemNo', 'itemNo', t('manager.modules.countingListCreation.columns.itemNo', {}, 'Varenr.'), {
            minWidth: 120,
            sortable: true,
            filterType: 'both'
        }),
        columnFactory.createTextColumn('description1', 'description1', t('common.fields.description', {}, 'Beskrivelse'), {
            minWidth: 200,
            sortable: true,
            filterType: 'both'
        }),
        columnFactory.createTextColumn('itemBarCode', 'itemBarCode', t('manager.modules.countingListCreation.columns.itemBarCode', {}, 'Strekkode'), {
            minWidth: 130,
            sortable: true,
            filterType: 'both'
        }),
        columnFactory.createTextColumn('warehouseID', 'warehouseID', t('warehouse.general.warehouse', {}, 'Lager'), {
            minWidth: 80,
            sortable: true,
            filterType: 'both'
        }),
        columnFactory.createTextColumn('binCode', 'binCode', t('warehouse.general.bin', {}, 'Lokasjon'), {
            minWidth: 120,
            sortable: true,
            filterType: 'both'
        }),
        columnFactory.createNumberColumn('binQuantity', 'binQuantity', t('manager.modules.countingListCreation.columns.binQuantity', {}, 'Beholdning'), {
            minWidth: 100,
            sortable: true,
            filterType: 'both'
        }),
        columnFactory.createTextColumn('supplierItemNo', 'supplierItemNo', t('manager.modules.countingListCreation.columns.supplierItemNo', {}, 'Lev.varenr.'), {
            minWidth: 120,
            sortable: true,
            filterType: 'both',
            visible: false
        }),
        columnFactory.createTextColumn('itemUnitCode', 'itemUnitCode', t('manager.modules.countingListCreation.columns.itemUnitCode', {}, 'Enhet'), {
            minWidth: 70,
            sortable: true,
            filterType: 'both',
            visible: false
        }),
        columnFactory.createTextColumn('itemGrp1', 'itemGrp1', t('manager.modules.countingListCreation.columns.itemGrp1', {}, 'Varegruppe 1'), {
            minWidth: 110,
            sortable: true,
            filterType: 'both',
            visible: false
        }),
        columnFactory.createTextColumn('itemGrp2', 'itemGrp2', t('manager.modules.countingListCreation.columns.itemGrp2', {}, 'Varegruppe 2'), {
            minWidth: 110,
            sortable: true,
            filterType: 'both',
            visible: false
        }),
        columnFactory.createTextColumn('itemGrp3', 'itemGrp3', t('manager.modules.countingListCreation.columns.itemGrp3', {}, 'Varegruppe 3'), {
            minWidth: 110,
            sortable: true,
            filterType: 'both',
            visible: false
        }),
        columnFactory.createTextColumn('stdLocationName', 'stdLocationName', t('manager.modules.countingListCreation.columns.stdLocationName', {}, 'Std.lokasjon'), {
            minWidth: 110,
            sortable: true,
            filterType: 'both',
            visible: false
        }),
        columnFactory.createDateColumn('lastCountedDate', 'lastCountedDate', t('manager.modules.countingListCreation.columns.lastCountedDate', {}, 'Sist telt'), {
            minWidth: 110,
            sortable: true,
            filterType: 'both'
        }),
        columnFactory.createDateColumn('lastMovementDate', 'lastMovementDate', t('manager.modules.countingListCreation.columns.lastMovementDate', {}, 'Sist bevegelse'), {
            minWidth: 120,
            sortable: true,
            filterType: 'both',
            visible: false
        }),
        // Kun synlig når «Vis rader på aktiv telleliste» er på — viser hvilken liste raden er opptatt av
        ...(includeOnActiveList ? [
            columnFactory.createTextColumn('activeCountListBatchID', 'activeCountListBatchID', t('manager.modules.countingListCreation.columns.activeCountList', {}, 'På telleliste'), {
                minWidth: 130,
                sortable: false,
                filterType: 'none'
            })
        ] : []),
    ], [columnFactory, t, includeOnActiveList]);

    // Table actions
    const tableActions: TableAction<ItemBinCountingRow>[] = useMemo(() => [
        {
            id: 'create-counting-list',
            label: t('manager.modules.countingListCreation.actions.createCountList', { '0': selectedCount }, `Opprett telleliste (${selectedCount})`),
            icon: ClipboardList,
            onClick: () => handleCreateCountingList(),
            variant: 'primary' as const,
            disabled: () => selectedCount === 0
        },
        {
            id: 'refresh',
            label: t('common.buttons.refresh', {}, 'Oppdater'),
            icon: RefreshCw,
            onClick: () => refreshData(),
            variant: 'secondary' as const
        }
    ], [selectedCount, handleCreateCountingList, refreshData, t]);

    // Toggles as toolbar elements with active styling
    const toolbarToggles = useMemo(() => (
        <div className="flex items-center gap-2">
            <button
                onClick={() => setIncludeZeroStock(prev => !prev)}
                className={`px-3 py-2 text-xs font-semibold rounded-lg border flex items-center gap-1.5 transition-all duration-200 ${
                    includeZeroStock
                        ? 'text-white bg-blue-600 border-blue-600 hover:bg-blue-700'
                        : 'text-blue-700 bg-white border-blue-300 hover:bg-blue-50 hover:shadow-md'
                }`}
                title={includeZeroStock
                    ? t('manager.modules.countingListCreation.toggle.hideZeroStock', {}, 'Skjul lokasjoner med 0 beholdning')
                    : t('manager.modules.countingListCreation.toggle.showZeroStock', {}, 'Vis lokasjoner med 0 beholdning')}
            >
                {includeZeroStock ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                {t('manager.modules.countingListCreation.toggle.includeZeroStock', {}, 'Inkluder nullbeholdning')}
            </button>
            <button
                onClick={() => setIncludeOnActiveList(prev => !prev)}
                className={`px-3 py-2 text-xs font-semibold rounded-lg border flex items-center gap-1.5 transition-all duration-200 ${
                    includeOnActiveList
                        ? 'text-white bg-blue-600 border-blue-600 hover:bg-blue-700'
                        : 'text-blue-700 bg-white border-blue-300 hover:bg-blue-50 hover:shadow-md'
                }`}
                title={includeOnActiveList
                    ? t('manager.modules.countingListCreation.toggle.hideOnActiveList', {}, 'Skjul rader som allerede ligger på en aktiv telleliste')
                    : t('manager.modules.countingListCreation.toggle.showOnActiveList', {}, 'Vis rader som allerede ligger på en aktiv telleliste (nedtonet, kan ikke velges)')}
            >
                {includeOnActiveList ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                {t('manager.modules.countingListCreation.toggle.includeOnActiveList', {}, 'Vis rader på aktiv telleliste')}
            </button>
        </div>
    ), [includeZeroStock, includeOnActiveList, t]);

    const formatNumber = (n: number) => n.toLocaleString('nb-NO');

    return (
        <div className="flex flex-col h-full overflow-hidden bg-gray-50">
            {/* Sub-header with back button and page title */}
            <div className="sticky top-0 z-20 bg-white border-b border-gray-300">
                <div className="px-4 py-2 flex items-center gap-2">
                    <BackButton onClick={onBack} />
                    <ClipboardList className="w-5 h-5 text-blue-600" />
                    <h1 className="text-lg font-semibold text-gray-900">{t('manager.modules.countingListCreation.page.title', {}, 'Opprett tellelister')}</h1>
                </div>
            </div>

            {/* Utvalgsbanner — «velg alle som matcher filteret» (Gmail-mønster) */}
            {showSelectAllBanner && (
                <div className="flex flex-wrap items-center gap-3 px-4 py-2 bg-blue-50 border-b border-blue-200 text-sm text-blue-900">
                    <CheckSquare className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span>
                        {t('manager.modules.countingListCreation.bulk.pageSelected', { '0': formatNumber(selectablePageKeys.length) }, `${formatNumber(selectablePageKeys.length)} rader på denne siden er valgt.`)}
                    </span>
                    <button
                        onClick={handleSelectAllMatching}
                        className="px-3 py-1.5 min-h-[36px] text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        {t('manager.modules.countingListCreation.bulk.selectAllMatching', { '0': formatNumber(totalRecords) }, `Velg alle ${formatNumber(totalRecords)} rader som matcher filteret`)}
                    </button>
                </div>
            )}
            {filterSelection && (
                <div className="flex flex-wrap items-center gap-3 px-4 py-2 bg-blue-600 border-b border-blue-700 text-sm text-white">
                    <CheckSquare className="w-4 h-4 flex-shrink-0" />
                    <span className="font-medium">
                        {t('manager.modules.countingListCreation.bulk.allMatchingSelected', { '0': formatNumber(filterSelection.totalRows) }, `Alle ${formatNumber(filterSelection.totalRows)} rader som matcher filteret er valgt`)}
                        {excludedRowKeys.size > 0 && (
                            <span className="font-normal opacity-90">
                                {' '}{t('manager.modules.countingListCreation.bulk.excludedCount', { '0': excludedRowKeys.size }, `(${excludedRowKeys.size} fjernet manuelt)`)}
                            </span>
                        )}
                    </span>
                    <span className="opacity-80">
                        {t('manager.modules.countingListCreation.bulk.filterHint', {}, 'Endres filteret, nullstilles utvalget.')}
                    </span>
                    <button
                        onClick={handleClearSelection}
                        className="ml-auto px-3 py-1.5 min-h-[36px] text-sm font-semibold text-blue-700 bg-white rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-1.5"
                    >
                        <X className="w-4 h-4" />
                        {t('manager.modules.countingListCreation.bulk.clearSelection', {}, 'Fjern utvalg')}
                    </button>
                </div>
            )}

            <div className="flex-1 min-h-0">
                <WarehouseTable<ItemBinCountingRow>
                    data={serverData}
                    columns={columns}
                    config={{
                        showPagination: true,
                        showColumnConfig: true,
                        showGlobalSearch: false,
                        showFilters: true,
                        showExport: false,
                        showPrint: false,
                        showSummary: false,
                        defaultPageSize: 50,
                        allowColumnReorder: true,
                        allowColumnResize: true,
                        stickyHeader: true,
                        compactMode: true,
                        persistenceKey: 'counting-list-creation-table',
                        allowRowSelection: {
                            mode: 'multi',
                            selectAllMode: 'current-page'
                        }
                    }}
                    serverPagination={{
                        enabled: true,
                        totalRecords: totalRecords,
                        onFetchData: handleServerFetch
                    }}
                    selectedRows={selectedRowKeys}
                    onSelectionChange={handleSelectionChange}
                    getRowId={getRowId}
                    tableActions={tableActions}
                    toolbarLeft={toolbarToggles}
                    getRowClassName={(row) => row.activeCountListBatchID ? 'opacity-50' : undefined}
                    isRowSelectable={(row) => !row.activeCountListBatchID}
                    loading={isLoading}
                    error={error || undefined}
                />
            </div>

            {/* Create Counting List Modal */}
            {showCreateModal && selectedCount > 0 && (
                <CreateCountingListModal
                    isOpen={showCreateModal}
                    onClose={() => setShowCreateModal(false)}
                    selection={selection}
                    onSuccess={handleCreateSuccess}
                />
            )}
        </div>
    );
};

export default CountingListCreation;
